/**
 * LEHeader - Header component for Loan Estimate viewer
 *
 * Displays lender info, date issued, loan ID, and basic borrower/property info
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { MapPin } from 'lucide-react';
import type { ParsedLoanEstimate } from './types';

interface LEHeaderProps {
  data: ParsedLoanEstimate;
  className?: string;
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

export const LEHeader: React.FC<LEHeaderProps> = ({ data, className }) => {
  return (
    <div className={cn('space-y-4', className)}>
      {/* Borrower and Property Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
        {/* Borrower */}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            Borrower
          </p>
          <p className="font-medium">{data.borrowerName || 'Unknown'}</p>
          {data.coborrowerName && (
            <p className="text-sm text-muted-foreground">
              Co-Borrower: {data.coborrowerName}
            </p>
          )}
        </div>

        {/* Property */}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            Property
          </p>
          <div className="flex items-start gap-1.5">
            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">{data.propertyAddress || 'TBD'}</p>
              {data.estimatedPropertyValue > 0 && (
                <p className="text-sm text-muted-foreground">
                  Est. Value: {formatCurrency(data.estimatedPropertyValue)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LEHeader;
